// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
// Service
import { get, post, put } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Utils
import {
    InitialSubAccountState,
    SubAccountSchema,
    SubAccountFormFields,
} from "./Configuration.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
import PermissionConstants from "../../../configs/PermissionConstants";
import usePermissions from "../../../hooks/usePermissions";

const SubAccount = ({ id, onClose }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [sub_account, setSubAccount] = useState(InitialSubAccountState);
    const [formFields, setFormFields] = useState(SubAccountFormFields);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name && (field.type === "select" || field.type === "multiselect")
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const getGroupHeaders = async () => {
        spinService.on();
        const response = await get(EntityConstants.GROUP_HEADERS, "max=all");
        const data = await response.json();
        attachOptions("group_header_id", data);
        spinService.off();
    };

    const getMainGroups = async (group_header_id) => {
        spinService.on();
        const response = await get(EntityConstants.MAIN_GROUPS, `max=all&group_header_id=${group_header_id}`);
        const data = await response.json();
        attachOptions("main_group_id", data);
        spinService.off();
    };

    const getSubAccount = async (id) => {
        spinService.on();
        const response = await get(EntityConstants.SUB_ACCOUNTS + `/${id}`);
        const data = await response.json();
        setSubAccount(data);
        getMainGroups(data.group_header_id);
        spinService.off();
    };

    const localHandleChange = (e) => {
        if (e.target.name === "group_header_id") {
            getMainGroups(e.target.value);
        }
    };

    const handleSubmit = async (values) => {
        spinService.on();
        const isNewSubAccount = values.id === undefined;
        const url = isNewSubAccount ? EntityConstants.SUB_ACCOUNTS : EntityConstants.SUB_ACCOUNTS + `/${values.id}`;
        const method = isNewSubAccount ? post : put;
        const response = await method(url, { sub_account: values });
        if ([200, 201].indexOf(response.status) > -1) {
            toast.success(isNewSubAccount ? MessageContants.GROUP_HEADER_CREATED : MessageContants.GROUP_HEADER_UPDATED);
            onClose();
        }
        spinService.off();
    };

    useEffect(() => {
        getGroupHeaders();
        if (id !== undefined) {
            getSubAccount(Number(id));
        }
    }, [id]);

    return (
        <Formik
            enableReinitialize
            initialValues={sub_account}
            validationSchema={SubAccountSchema}
            onSubmit={(values) => handleSubmit(values)}
        >
            {({
                values,
                touched,
                errors,
                dirty,
                isSubmitting,
                handleChange,
                handleBlur,
                handleSubmit,
            }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <StyledDrawerForm>
                        <Grid
                            container
                            maxWidth="sm"
                            rowSpacing={1}
                            columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                        >
                            {formFields.map((formField, index) => {
                                return (
                                    <Grid key={index} item xs={12} sm={12} md={6} lg={6}>
                                        <GoForm
                                            config={formField}
                                            values={values}
                                            touched={touched}
                                            errors={errors}
                                            handleChange={(e) => { handleChange(e); localHandleChange(e) }}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                        <Box sx={{ p: 2, textAlign: "right" }}>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<Iconify icon="eva:close-circle-outline" />}
                                onClick={() => onClose()}
                            >
                                Close
                            </Button>
                            &nbsp;&nbsp;
                            {((values.id === undefined && permissions.has(PermissionConstants.SUB_ACCOUNT_CREATE)) || (values.id !== undefined && permissions.has(PermissionConstants.SUB_ACCOUNT_UPDATE))) &&
                                <Button
                                    variant="contained"
                                    startIcon={<Iconify icon="eva:save-fill" />}
                                    type="submit"
                                >
                                    {values.id === undefined ? 'Save' : 'Update'}
                                </Button>
                            }
                        </Box>
                    </StyledDrawerForm>
                </Form>
            )}
        </Formik>
    );
};

SubAccount.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default SubAccount;
